import { ScrollingModule } from '@angular/cdk/scrolling';
import {
	AfterContentInit,
	Component,
	ComponentRef,
	ContentChildren,
	DestroyRef,
	Directive,
	ElementRef,
	EmbeddedViewRef,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	Output,
	QueryList,
	reflectComponentType,
	TemplateRef,
	Type,
	ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Subject, switchMap, take, takeUntil } from 'rxjs';

import { ScreenService, ScreenSize } from '~ng-common-lib/cdk/screen.service';

import { ClientReachBottomMenuComponent } from '../components/bottom-menu/bottom-menu.component';
import {
	ClientFloatingMenuComponent,
	MenuFloatingPosition,
	WidthSize,
} from '../components/floating/floating.component';
import { ClientPopupMenuComponent } from '../components/popup/popup.component';
import { injectMenuConfiguration } from '../tokens/menu-config';
import { MenuDesktopType } from '../types/desktop';
import { BaseMenu } from '../types/menu';

enum MenuType {
	FLOATING = 'FLOATING',
	POPUP = 'POPUP',
	MOBILE = 'MOBILE',
}

interface MenuDataMap {
	[MenuType.FLOATING]: {
		anchor: HTMLElement | null;
		widthSize: WidthSize | null;
		maxHeight: string | null;
		fullHeight: boolean;
		lightBg: boolean;
		hasPaddingTop: boolean;
		hasBackdrop: boolean | null;
		forcePosition: MenuFloatingPosition | null;
	};
	[MenuType.POPUP]: {
		lightBg: boolean;
		minHeight: string | null;
	};
	[MenuType.MOBILE]: {
		newDesign: true;
		transitionTimeMs?: number;
		showHeader: boolean;
		showFooter: boolean;
		fullHeight: boolean;
		lightBg: boolean;
	};
}
type MenuData<TYPE extends MenuType> = TYPE extends keyof MenuDataMap
	? MenuDataMap[TYPE]
	: Record<string, never>;

interface MenuDataToRecoverMap {
	[MenuType.MOBILE]: {
		transitionTimeMs: number;
	};
}
type MenuDataToRecover<TYPE extends MenuType> = TYPE extends keyof MenuDataToRecoverMap
	? MenuDataToRecoverMap[TYPE]
	: Record<string, never>;

const DATA_TO_RECOVER_MAP: Record<MenuType, Array<keyof MenuDataToRecover<MenuType>>> = {
	[MenuType.FLOATING]: [],
	[MenuType.POPUP]: [],
	[MenuType.MOBILE]: ['transitionTimeMs'],
};

@Directive({
	selector: '[clientReachMenuSection]',
	standalone: true,
	exportAs: 'clientReachMenuSection',
})
export class ClientReachMenuSectionDirective<T extends string> implements OnDestroy {
	private readonly screenService = inject(ScreenService);
	private readonly viewContainerRef = inject(ViewContainerRef);
	private readonly templateRef = inject<TemplateRef<{ isMobile: boolean }>>(
		TemplateRef<{ isMobile: boolean }>
	);

	private readonly viewDestroyed$$ = new Subject<void>();

	private view?: EmbeddedViewRef<unknown>;
	private componentRef?: ComponentRef<BaseMenu>;
	private currentMenuType?: MenuType;

	@Input('clientReachMenuSection')
	public section!: T | null;

	@Input('clientReachMenuSectionDesktopType')
	public desktopType!: MenuDesktopType | `${MenuDesktopType}`;

	@Input('clientReachMenuSectionAlwaysUseDesktop')
	public alwaysUseDesktop = false;

	@Input('clientReachMenuSectionAlwaysUseMobile')
	public alwaysUseMobile = false;

	@Input('clientReachMenuSectionShowHeader')
	public showHeader?: boolean;

	@Input('clientReachMenuSectionShowFooter')
	public showFooter?: boolean;

	@Input('clientReachMenuSectionLightBg')
	public lightBg?: boolean;

	@Input('clientReachMenuSectionHasPaddingTop')
	public hasPaddingTop?: boolean = false;

	private _hasBackdrop?: boolean = true;

	@Input('clientReachMenuSectionHasBackdrop')
	public set hasBackdrop(hasBackdrop: boolean | undefined) {
		this._hasBackdrop = hasBackdrop;
	}
	public get hasBackdrop(): boolean {
		return this._hasBackdrop ?? true;
	}

	@Input('clientReachMenuSectionForcePosition')
	public forcePosition: MenuFloatingPosition | undefined;

	private _anchor: HTMLElement | null = null;

	@Input('clientReachMenuSectionAnchor')
	public set anchor(anchor: HTMLElement | undefined) {
		this._anchor = anchor ?? null;
	}

	public get anchor(): HTMLElement | undefined {
		return this._anchor || undefined;
	}

	private _widthSize: WidthSize | null = null;

	@Input('clientReachMenuSectionWidthSize')
	public set widthSize(widthSize: WidthSize | undefined) {
		this._widthSize = widthSize ?? 'DEFAULT';
	}

	public get widthSize(): WidthSize | undefined {
		return this._widthSize || undefined;
	}

	@Input('clientReachMenuSectionDesktopFullHeight')
	public desktopFullHeight = false;

	private _maxHeight: string | null = null;

	@Input('clientReachMenuSectionMaxHeight')
	public set maxHeight(maxHeight: string | null) {
		this._maxHeight = maxHeight || null;
	}

	public get maxHeight(): string | null {
		return this._maxHeight;
	}

	private _fullHeight = false;

	@Input('clientReachMenuSectionFullHeight')
	public set fullHeight(fullHeight: boolean) {
		this._fullHeight = fullHeight;
	}

	public get fullHeight(): boolean {
		return this._fullHeight;
	}

	private _minHeight: string | null = null;

	@Input('clientReachMenuSectionMinHeight')
	public set minHeight(minHeight: string | null) {
		this._minHeight = minHeight ?? null;
	}

	public get minHeight(): string | null {
		return this._minHeight;
	}

	@Output('closed')
	public readonly closed$$ = new EventEmitter<void>();

	@Output('opened')
	public readonly opened$$ = new EventEmitter<void>();

	ngOnDestroy(): void {
		this.viewDestroyed$$.next();
		this.viewDestroyed$$.complete();
	}

	public async close(immediatelly = false): Promise<void> {
		if (this.currentMenuType === MenuType.MOBILE) {
			const instance = this.componentRef?.instance as
				| ClientReachBottomMenuComponent
				| undefined;

			if (instance) {
				instance._emitToOnboarding = false;

				if (immediatelly) {
					instance.transitionTimeMs = 0;
				}
			}
		}
		this.componentRef?.instance.close();
	}

	public async open(immediatelly = false, anchor?: HTMLElement): Promise<void> {
		this.currentMenuType = this.getMenuType();
		const { component, data } = this.getMenuFromType(
			this.currentMenuType,
			immediatelly,
			anchor
		);

		this.view = this.viewContainerRef.createEmbeddedView(this.templateRef, {
			isMobile: this.screenService.$screen() === ScreenSize.SMALL,
		});
		const projectableNodes = this.getProjectableNodes(component);

		this.componentRef = this.viewContainerRef.createComponent(component, {
			projectableNodes,
		});

		const instance = this.componentRef.instance;
		const dataToOverride = Object.entries(data);
		const keysToRecover = DATA_TO_RECOVER_MAP[this.currentMenuType];
		const dataToRecover = keysToRecover.map((key) => {
			const value = instance[key as keyof typeof instance];
			return {
				key,
				value,
			};
		});
		// if((instance as unknown as Record<string, unknown>)[key] === undefined) {
		// 				//try if input is signal
		// 				const signalPropName = `$${key}`;
		// 				const maybeSignal = (instance as unknown as Record<string, unknown>)[signalPropName];
		// 				if (maybeSignal) {
		// 					(maybeSignal as WritableSignal<typeof value>).set(value);
		// 					continue;
		// 				}
		// 			} else {
		// 				(instance as unknown as Record<string, unknown>)[key] = value;
		// 			}
		for (const [key, value] of dataToOverride) {
			(instance as unknown as Record<string, unknown>)[key] = value;
		}

		instance.opened$$.pipe(take(1), takeUntil(this.viewDestroyed$$)).subscribe(() => {
			for (const { key, value } of dataToRecover) {
				(instance as unknown as Record<string, unknown>)[key] = value;
			}

			this.componentRef?.changeDetectorRef.detectChanges();
			this.opened$$.emit();
		});

		instance.closed$$.pipe(take(1), takeUntil(this.viewDestroyed$$)).subscribe(() => {
			this.clear(true);
		});

		this.componentRef?.changeDetectorRef?.detectChanges();

		instance.open();
	}

	public clear(emitClose = false): void {
		this.viewDestroyed$$.next();
		this.componentRef?.destroy();
		this.viewContainerRef.clear();
		this.view?.destroy();
		this.currentMenuType = undefined;

		if (emitClose) {
			this.closed$$.emit();
		}
	}

	private getProjectableNodes(component: Type<BaseMenu>): Node[][] {
		// https://angular.io/api/core/reflectComponentType
		const mirror = reflectComponentType(component);

		if (!mirror) {
			return [];
		}

		const selectors = mirror.ngContentSelectors;

		const rootNodes = this.view?.rootNodes ?? [];
		const nodes = [...rootNodes];

		const projectableNodes: Node[][] = Array.from({ length: selectors.length }, () => []);
		const wildcardIdx = selectors.findIndex((selector) => selector === '*');

		// https://stackoverflow.com/a/61890918/6951173
		nodes.forEach((node: ChildNode) => {
			try {
				if (node instanceof Element) {
					const projectedToIdx = selectors.findIndex((selector) => {
						return node.getAttribute('ngProjectAs') === selector;
					});

					if (projectedToIdx !== -1) {
						projectableNodes[projectedToIdx]?.push(node);
						return;
					}
				}
			} catch (error) {}

			if (wildcardIdx > -1) {
				projectableNodes[wildcardIdx]?.push(node);
			}
		});

		return projectableNodes;
	}

	private getMenuFromType<TYPE extends MenuType>(
		type: TYPE,
		openImmediatelly: boolean,
		anchor?: HTMLElement
	): { component: Type<BaseMenu>; data: MenuData<TYPE> } {
		switch (type) {
			case MenuType.FLOATING: {
				const data: MenuData<MenuType.FLOATING> = {
					anchor: anchor || this.anchor || null,
					widthSize: this.widthSize || null,
					maxHeight: this.maxHeight,
					fullHeight: this.desktopFullHeight,
					lightBg: this.lightBg ?? true,
					hasPaddingTop: this.hasPaddingTop ?? false, // default: false
					hasBackdrop: this.hasBackdrop ?? true, // default: true
					forcePosition: this.forcePosition || null,
				};
				return {
					component: ClientFloatingMenuComponent as Type<BaseMenu>,
					data,
				} as { component: Type<BaseMenu>; data: MenuData<TYPE> };
			}

			case MenuType.POPUP: {
				const data: MenuData<MenuType.POPUP> = {
					lightBg: this.lightBg ?? true,
					minHeight: this.minHeight,
				};
				return { component: ClientPopupMenuComponent as Type<BaseMenu>, data } as {
					component: Type<BaseMenu>;
					data: MenuData<TYPE>;
				};
			}

			default: {
				const data: MenuData<MenuType.MOBILE> = {
					newDesign: true,
					transitionTimeMs: 0,
					showHeader: this.showHeader !== false, // default: true
					showFooter: !!this.showFooter, // default: false,
					fullHeight: this.fullHeight, // default: false,
					lightBg: this.lightBg ?? true, // default: true,
				};
				if (!openImmediatelly) {
					delete data.transitionTimeMs;
				}
				return {
					component: ClientReachBottomMenuComponent as Type<BaseMenu>,
					data,
				} as { component: Type<BaseMenu>; data: MenuData<TYPE> };
			}
		}
	}

	private getMenuType(): MenuType {
		if (
			this.alwaysUseMobile ||
			(!this.alwaysUseDesktop && this.screenService.$screen() === ScreenSize.SMALL)
		) {
			return MenuType.MOBILE;
		}

		return this.desktopType === MenuDesktopType.POPUP ? MenuType.POPUP : MenuType.FLOATING;
	}
}

/**
 * **Limitations**
 * - You can't directly use <ng-container> as a direct children
 * of the [clientReachMenuSection] directive.
 * - You can't directly use *ngIf (or other structural directives)
 * as a direct children of the [clientReachMenuSection] directive.
 *
 * ---
 *
 * **Tips**
 * - Always wrap the title and body of the menu in a
 * `<div>` tag (except `<ng-container>`) without structural directives.
 *
 * ---
 *
 * **Usage**
 *
 * If you want to use the asterisc notation, you need to
 * add the directive to an `ng-container` element.
 *
 * ```html
 * <client-reach-menu #menu [activeSection]="activeSection">
 * 	 <ng-container *clientReachMenuSection="sectionId; desktopType: sectionDesktopType; anchor: sectionAnchor">
 * 	   ...
 *   </ng-container>
 * </client-reach-menu>
 * ```
 *
 * Else, you can use the directive in a ng-template using the regular
 * directive notation.
 *
 * ```html
 * <client-reach-menu #menu [activeSection]="activeSection">
 * 	 <ng-template
 * 	 	 [clientReachMenuSection]="sectionId"
 * 	 	 [clientReachMenuSectionDesktopType]="sectionDesktopType"
 * 	 	 [reachMenuAnchor]="sectionAnchor"
 *   >
 * 	   ...
 *   </ng-template>
 * </client-reach-menu>
 * ```
 *
 * ---
 *
 * **Example**
 * ```html
 * <button #anchor (click)="activeSection = activeSection === '1' ? '2' : '1'">
 *   Toggle Section
 * </button>
 *
 * <client-reach-menu #menu [activeSection]="activeSection">
 *   <ng-container *clientReachMenuSection="'1'; desktopType: sectionDesktopType; anchor: sectionAnchor">
 *     <div ngProjectAs=[~left]>Left 1</div>
 *     <div ngProjectAs=[~title]>Title 1</div>
 *     <div ngProjectAs=[~right]>Right 1</div>
 *     <div>This is the section 1 body</div>
 *   </ng-container>
 *   <ng-container *clientReachMenuSection="'2'; desktopType: sectionDesktopType; anchor: sectionAnchor">
 *     <div ngProjectAs=[~left]>Left 2</div>
 *     <div ngProjectAs=[~title]>Title 2</div>
 *     <div ngProjectAs=[~right]>Right 2</div>
 *     <div>This is the section 2 body</div>
 *   </ng-container>
 * </client-reach-menu>
 * ```
 */
@Component({
	selector: 'client-reach-menu',
	template: '<ng-content></ng-content>',
	styles: [':host { display: none; }'],
	standalone: true,
	imports: [ScrollingModule],
})
export class ClientReachMenuComponent<T extends string = string>
	extends BaseMenu
	implements AfterContentInit, OnDestroy
{
	private readonly destroyRef = inject(DestroyRef);
	private readonly config = injectMenuConfiguration();

	@ContentChildren(ClientReachMenuSectionDirective<T>)
	protected readonly sectionList!: QueryList<ClientReachMenuSectionDirective<T>>;

	private currentSection?: ClientReachMenuSectionDirective<T>;

	private readonly activeSection$$ = new BehaviorSubject<T | null>(null);
	private readonly activeSection$ = this.activeSection$$.asObservable();

	private readonly componentChanged$$ = new Subject<void>();
	private _onboardingConditionAdded = false;

	@Input()
	public set activeSection(section: T | null) {
		this.activeSection$$.next(section);
	}

	public get activeSection(): T | null {
		return this.activeSection$$.value;
	}

	@Output('closed')
	public override readonly closed$$ = new EventEmitter<void>();

	@Output('opened')
	public override readonly opened$$ = new EventEmitter<void>();

	private alreadyOpened = false;
	private _fullyOpened = false;

	private set fullyOpened(fullyOpened: boolean) {
		this._fullyOpened = fullyOpened;
	}

	public get fullyOpened(): boolean {
		return this._fullyOpened;
	}

	ngAfterContentInit(): void {
		this.sectionList?.changes
			.pipe(
				map(() => {
					return this.sectionList?.toArray();
				}),
				switchMap((sections) => {
					return this.activeSection$.pipe(
						map((activeSectionKey) => {
							return (
								sections.find((section) => {
									return section.section === activeSectionKey;
								}) ??
								sections.find((section) => {
									return !section.section;
								}) ??
								null
							);
						})
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((activeSection) => {
				this.changeActiveSection(activeSection);
			});

		this.sectionList?.notifyOnChanges();
	}

	ngOnDestroy(): void {
		this.componentChanged$$.next();
		this.componentChanged$$.complete();
	}

	// TODO: Change this so it can be a 'trigger' component (to mark it as active to change its style)
	open(anchor?: HTMLElement | ElementRef<HTMLElement>): void {
		const typedAnchor = anchor instanceof ElementRef ? anchor.nativeElement : anchor;

		if (!this.alreadyOpened) {
			this.alreadyOpened = true;

			this.listenForOpen(this.currentSection || null);
			this.listenForClose(this.currentSection || null);

			this.currentSection?.open(false, typedAnchor);
		}
	}

	close(): void {
		this.alreadyOpened = false;
		this.fullyOpened = false;
		this.currentSection?.close();
	}

	public _emitOpen(): void {
		this.opened$$.emit();
		this.alreadyOpened = true;
		this.fullyOpened = true;

		this.config.onMenuOpened?.();
		this._onboardingConditionAdded = true;
	}

	public _emitClose(): void {
		this.closed$$.emit();
		this.alreadyOpened = false;
		this.fullyOpened = false;

		if (this._onboardingConditionAdded) {
			this.config.onMenuClosed?.();
			this._onboardingConditionAdded = false;
		}
	}

	private changeActiveSection(section: ClientReachMenuSectionDirective<T> | null): void {
		this.componentChanged$$.next();

		const prev = this.currentSection;
		this.currentSection = section || undefined;

		if (this.alreadyOpened) {
			// Close prev, open current

			const sectionToListenTo = section ?? prev;
			this.listenForClose(sectionToListenTo || null);

			if (section) {
				prev?.clear();
			} else {
				prev?.close();
			}

			section?.open(this.fullyOpened);
		}
	}

	private listenForClose(section: ClientReachMenuSectionDirective<T> | null): void {
		section?.closed$$.pipe(take(1), takeUntil(this.componentChanged$$)).subscribe(() => {
			this._emitClose();
		});
	}

	private listenForOpen(section: ClientReachMenuSectionDirective<T> | null): void {
		section?.opened$$.pipe(take(1), takeUntil(this.componentChanged$$)).subscribe(() => {
			this._emitOpen();
		});
	}
}
